import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Website/Navbar';
import Footer from '../components/Website/Footer';
import { blogAPI } from '../services/api';
import './Blog.css';

interface Category {
  id: number;
  name: string;
  slug: string;
  post_count: number;
}

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  category: number;
  category_name: string;
  category_slug: string;
  author_name: string;
  image_display: string;
  read_time: number;
  published_at: string;
}

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchPosts();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPosts();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await blogAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory !== 'all') {
        const category = categories.find(c => c.name === selectedCategory);
        if (category) params.category = category.slug;
      }
      const response = await blogAPI.getPosts(params);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError('Failed to load blog posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
  };

  const handleReadMore = (slug: string) => {
    navigate(`/blog/${slug}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const categoryList = ['all', ...categories.map(c => c.name)];

  // SVG Icons
  const Icons = {
    Calendar: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    Clock: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    User: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    ArrowRight: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    ),
    Search: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
    Error: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <circle cx="12" cy="16" r="0.5" fill="currentColor"/>
      </svg>
    ),
    Empty: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
  };

  if (loading && posts.length === 0) {
    return (
      <>
        <Navbar />
        <div className="blog-loading">
          <div className="blog-spinner"></div>
          <p>Loading articles...</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="blog-page">
        <div className="blog-container">
          <div className="blog-header">
            <div className="blog-header-badge">Insights & Stories</div>
            <h1 className="blog-title">Our Blog</h1>
            <p className="blog-subtitle">
              Insights, trends, and stories from the world of technology
            </p>
          </div>

          <div className="blog-filters">
            <div className="blog-search-wrapper">
              <span className="blog-search-icon">
                <Icons.Search />
              </span>
              <input
                type="text"
                className="blog-search-input"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="blog-category-filters">
              {categoryList.map(category => (
                <button
                  key={category}
                  className={`blog-category-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => handleCategorySelect(category)}
                >
                  {category === 'all' ? 'All' : category}
                  {category !== 'all' && categories.find(c => c.name === category) && (
                    <span className="blog-category-count">
                      ({categories.find(c => c.name === category)?.post_count || 0})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="blog-error">
              <div className="blog-error-icon">
                <Icons.Error />
              </div>
              <p>{error}</p>
              <button className="blog-retry-btn" onClick={fetchPosts}>Try Again</button>
            </div>
          )}

          {!error && posts.length === 0 && !loading && (
            <div className="blog-empty">
              <div className="blog-empty-icon">
                <Icons.Empty />
              </div>
              <h3>No articles found</h3>
              <p>No blog posts match your search criteria.</p>
              <button className="blog-clear-btn" onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}>Clear filters</button>
            </div>
          )}

          <div className="blog-grid">
            {posts.map(post => (
              <div key={post.id} className="blog-card">
                {post.image_display && (
                  <div className="blog-card-image">
                    <img src={post.image_display} alt={post.title} />
                  </div>
                )}
                <div className="blog-card-content">
                  <span className="blog-card-category">{post.category_name}</span>
                  <h2 className="blog-card-title">{post.title}</h2>
                  <p className="blog-card-excerpt">{post.excerpt}</p>
                  <div className="blog-card-meta">
                    <span className="blog-meta-item">
                      <Icons.Calendar />
                      {formatDate(post.published_at)}
                    </span>
                    <span className="blog-meta-item">
                      <Icons.Clock />
                      {post.read_time} min read
                    </span>
                    <span className="blog-meta-item">
                      <Icons.User />
                      {post.author_name}
                    </span>
                  </div>
                  <button 
                    className="blog-read-more" 
                    onClick={() => handleReadMore(post.slug)}
                  >
                    Read More
                    <Icons.ArrowRight />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {posts.length > 0 && (
            <div className="blog-load-more">
              <button className="blog-load-more-btn">
                Load More Articles
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Blog;